import { useFormik } from "formik";
import * as Yup from "yup";
import { V3Button, V3TertiaryButton } from "../Button";
import React, { useEffect, useRef, useState, type JSX } from "react";
import { useInView } from "react-intersection-observer";
import { UserCoverStyle } from "@authory-packages/core";
import { DropdownOptionCTA } from "../DropdownOptionCTA";
import { V3IconMagnifier, V3IconRefresh } from "../Icons";
import { V3IconBin } from "../Icons";
import { V3Loader } from "../Loader";
import { V3RadioButton } from "../OptionInput";
import { V3BodyGrey48 } from "../ReusableStyledComponents/V3BodyGrey48";
import { V3TextArea, V3TextInput } from "../TextInput/V3TextInput";
import { V3ModalBase } from "../V3ModalBase";
import { VerticalSpacing } from "../VerticalSpacing";
import { MediaEditingFormik } from "./V3MediaEditing.utils";
import { CCImageUploadWrapper, CCMEButtons, CCUnsplashResults, CCUnsplashScroller, CCUnsplashThumb } from "./V3MediaEditing.styles";
import { V3TertiaryCTAColor } from "../Button/V3TertiaryCTA.types";
import { unplashHook } from "../../hooks/unplashHook";
import { ModalLoadingSpinner } from "../ReusableStyledComponents/ModalLoadingSpinner";
import { UnsplashCollection } from "../../api/unsplash";
import { V3PanelWithPreview } from "../V3PanelWithPreview/V3PanelWithPreview";
import { MediaUploadWrapper } from "../MediaUploadWrapper";
import { V3DropdownCTAColor } from "../DropdownOptionCTA/DropdownOptionCTA.types";
import { ColorSwatch } from "../ColorSwatch";
import { ColorHexValidation } from "../../shared-logic";

interface V3MediaEditingModalProps extends V3MediaEditingModalContainerProps {
    unsplashPhotos: UnsplashCollection[];
    unsplashLoading: boolean;
    unsplashLoadMore: () => void;
    unsplashCanFetchmore: boolean;
    unsplashSearchHandler: (e: any) => void;
}

export const V3MediaEditingModal = ({
    modalOpen,
    onCloseHandler,
    title,
    description,
    style,
    color,
    image,
    unsplashId,
    modalTitle,
    modalDescription,
    unsplashPhotos,
    unsplashLoading,
    unsplashLoadMore,
    unsplashCanFetchmore,
    unsplashSearchHandler,
    onFileUploadHandler,
    onMediaEditingSubmit,
    showMediaPreview,
    showTextEdit,
    fallbackImageUrl,
    fallbackTitle,
    fallbackDescription,
    thumbnailImageKey,
    showRevert = true,
    isCollection = false,
    removeLabel = "Remove cover"
}: V3MediaEditingModalProps) => {
    const [mediaEditingSubmitting, setMediaEditingSubmitting] = useState(false);

    const [keyword, setKeyword] = useState("");
    const [localImage, setLocalImage] = useState(image);
    const [hasUploadedImage, setHasUploadedImage] = useState(
        thumbnailImageKey && thumbnailImageKey !== "none" ? false : style === UserCoverStyle.image
    );

    const formik = useFormik<MediaEditingFormik>({
        initialValues: {
            style: thumbnailImageKey && thumbnailImageKey !== "none" ? "media" : style,
            color,
            image,
            unsplashId,
            title,
            description,
            thumbnailImageKey
        },
        validationSchema: Yup.object().shape({
            color: ColorHexValidation
        }),
        onSubmit: async (data) => {
            try {
                setMediaEditingSubmitting(true);
                await onMediaEditingSubmit(data);
                setMediaEditingSubmitting(false);
                onCloseHandler();
            } catch {
                setMediaEditingSubmitting(false);
            }
        }
    });

    const hasUnsplashSelected = formik.values.style === UserCoverStyle.unsplash;
    const hasImageSelected = formik.values.style === UserCoverStyle.image;
    const hasColorSelected = formik.values.style === UserCoverStyle.color;
    const hasMediaSelected = formik.values.style === "media";
    const hasNoneSelected = formik.values.style === UserCoverStyle.none;
    const isNull = formik.values.style === null;

    const isValidUnsplash = hasUnsplashSelected && !!formik.values.unsplashId?.length;
    const hasColorError = formik.errors.hasOwnProperty("color") && formik.touched.hasOwnProperty("color");
    const isValidColor = hasColorSelected && !hasColorError;
    const isValidImage = hasImageSelected && hasUploadedImage;
    const isValidContent = hasMediaSelected && !!formik.values.thumbnailImageKey?.length;

    const isValidForm = isValidUnsplash || isValidColor || isValidImage || isValidContent || hasNoneSelected || isNull;

    useEffect(() => {
        if (hasUnsplashSelected) {
            unsplashSearchHandler({
                enabled: true
            });
        }
    }, [formik.values.style]);

    const refElement = useRef(null);

    const { ref, inView } = useInView({
        threshold: 0,
        rootMargin: "0px 0px 400px",
        initialInView: false
    });

    const search = () => {
        if (!keyword) return;

        unsplashSearchHandler({
            keyword: keyword,
            enabled: true
        });
    };

    if (inView && !unsplashLoading && unsplashCanFetchmore) {
        unsplashLoadMore();
    }

    const BtnsPanel = () => (
        <CCMEButtons>
            <div>
                {!mediaEditingSubmitting && (
                    <>
                        {showRevert && (showMediaPreview || showTextEdit) ? (
                            <>
                                {formik.values.style !== UserCoverStyle.none && formik.values.style !== null && (
                                    <V3TertiaryButton
                                        ctaColor={V3TertiaryCTAColor.gray}
                                        icon={<V3IconRefresh />}
                                        text="Revert to default preview"
                                        onClick={async () => {
                                            formik.setFieldValue("style", UserCoverStyle.none);
                                            if (fallbackImageUrl) setLocalImage(fallbackImageUrl);
                                            if (fallbackTitle) formik.setFieldValue("title", fallbackTitle);
                                            if (fallbackDescription) formik.setFieldValue("description", fallbackDescription);
                                        }}
                                    />
                                )}
                            </>
                        ) : (
                            <>
                                {formik.values.style !== UserCoverStyle.none && (
                                    <DropdownOptionCTA
                                        label={removeLabel}
                                        icon={<V3IconBin height={16} width={16} />}
                                        variant={V3DropdownCTAColor.red}
                                        onClick={async () => {
                                            formik.setFieldValue("style", UserCoverStyle.none);
                                            formik.submitForm();
                                        }}
                                    />
                                )}
                            </>
                        )}
                    </>
                )}
            </div>
            <div>
                {mediaEditingSubmitting ? (
                    <ModalLoadingSpinner />
                ) : (
                    <V3Button
                        text="Save"
                        autoWidth
                        disabled={!isValidForm}
                        onClick={() => {
                            formik.submitForm();
                        }}
                    />
                )}
            </div>
        </CCMEButtons>
    );

    return (
        <V3ModalBase
            isOpen={modalOpen}
            onClose={onCloseHandler}
            title={modalTitle}
            width={showMediaPreview ? "900px" : undefined}
            useHalfContentSplit={showMediaPreview}
            bottomPanel={<BtnsPanel />}
        >
            <V3PanelWithPreview
                title={formik.values.title}
                description={formik.values.description}
                imageSrc={localImage}
                showMediaPreview={showMediaPreview}
                isCollection={isCollection}
                mainPanel={
                    <>
                        <VerticalSpacing bottom={36}>
                            <V3BodyGrey48>{modalDescription}</V3BodyGrey48>
                        </VerticalSpacing>
                        <VerticalSpacing bottom={24}>
                            <V3RadioButton
                                name={UserCoverStyle.unsplash}
                                id={UserCoverStyle.unsplash}
                                label="Choose from our library"
                                checked={hasUnsplashSelected}
                                onChange={() => {
                                    const val = hasUnsplashSelected ? undefined : UserCoverStyle.unsplash;
                                    formik.setFieldValue("style", val);
                                }}
                            />
                            {hasUnsplashSelected && (
                                <>
                                    <VerticalSpacing top={18} bottom={12}>
                                        <V3TextInput
                                            placeholder="Search images..."
                                            isRounded
                                            icon={<V3IconMagnifier />}
                                            onChange={(e) => {
                                                setKeyword(e.target.value);
                                            }}
                                            onKeyDown={(e: any) => {
                                                if (e.key === "Enter") {
                                                    e.preventDefault();
                                                    search();
                                                }
                                            }}
                                        />
                                    </VerticalSpacing>
                                    <CCUnsplashScroller forceVisible="y" autoHide={false}>
                                        <CCUnsplashResults ref={refElement}>
                                            {unsplashPhotos.map((group, i) => (
                                                <React.Fragment key={i}>
                                                    {group.results.map(({ id, urls }, index) => (
                                                        <CCUnsplashThumb
                                                            key={index}
                                                            backgroundImg={urls.thumb}
                                                            selected={formik.values.unsplashId === id}
                                                            onClick={() => {
                                                                setLocalImage(urls.small);
                                                                formik.setFieldValue("unsplashId", id);
                                                                setHasUploadedImage(false);
                                                            }}
                                                        ></CCUnsplashThumb>
                                                    ))}
                                                </React.Fragment>
                                            ))}
                                            {unsplashPhotos[0]?.total === 0 && <V3BodyGrey48>No search results</V3BodyGrey48>}
                                            {unsplashLoading && <V3Loader height={36} />}
                                            {unsplashCanFetchmore && !unsplashLoading && <span ref={ref}></span>}
                                        </CCUnsplashResults>
                                    </CCUnsplashScroller>
                                </>
                            )}
                        </VerticalSpacing>
                        <VerticalSpacing bottom={24}>
                            <V3RadioButton
                                label="Upload image"
                                id={UserCoverStyle.image}
                                name={UserCoverStyle.image}
                                checked={hasImageSelected}
                                onChange={() => {
                                    const val = hasImageSelected ? undefined : UserCoverStyle.image;
                                    formik.setFieldValue("style", val);
                                }}
                            />
                            {hasImageSelected && (
                                <VerticalSpacing top={18}>
                                    <CCImageUploadWrapper image={hasUploadedImage ? localImage : undefined}>
                                        <div>
                                            {!hasUploadedImage && (
                                                <>
                                                    <svg width="48" height="49" viewBox="0 0 48 49" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <rect y=".5" width="48" height="48" rx="24" fill="#E2F0FF" />
                                                        <rect x="7" y="8" width="34" height="34" rx="17" fill="#BCDBFF" />
                                                        <path
                                                            d="M19 31a4.815 4.815 0 0 1-3.326-1.318 4.403 4.403 0 0 1-1.378-3.182c0-1.193.496-2.338 1.378-3.182A4.815 4.815 0 0 1 19 22c.295-1.313 1.157-2.466 2.397-3.207a5.97 5.97 0 0 1 2.025-.749 6.225 6.225 0 0 1 2.19.006c.722.131 1.408.39 2.02.76a5.36 5.36 0 0 1 1.543 1.397c.408.553.69 1.172.832 1.823.142.65.14 1.32-.007 1.97h1a3.5 3.5 0 1 1 0 7h-1"
                                                            stroke="#2E77FF"
                                                            strokeWidth="1.5"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                        />
                                                        <path
                                                            d="m21 28 3-3 3 3M24 25v9"
                                                            stroke="#2E77FF"
                                                            strokeWidth="1.5"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                        />
                                                    </svg>
                                                </>
                                            )}
                                        </div>
                                        <MediaUploadWrapper
                                            onFileUploadHandler={onFileUploadHandler}
                                            onUploadSuccess={(filepath) => {
                                                formik.setFieldValue("image", filepath);
                                                setHasUploadedImage(true);
                                                setLocalImage(filepath);
                                            }}
                                        />
                                    </CCImageUploadWrapper>
                                </VerticalSpacing>
                            )}
                        </VerticalSpacing>
                        {(!showMediaPreview || isCollection) && (
                            <VerticalSpacing bottom={24}>
                                <V3RadioButton
                                    label="Plain color"
                                    id={UserCoverStyle.color}
                                    name={UserCoverStyle.color}
                                    checked={hasColorSelected}
                                    onChange={() => {
                                        const val = hasColorSelected ? undefined : UserCoverStyle.color;
                                        formik.setFieldValue("style", val);
                                    }}
                                />
                                {hasColorSelected && (
                                    <VerticalSpacing top={18}>
                                        <div>
                                            <VerticalSpacing top={12}>
                                                <ColorSwatch formik={formik} />
                                            </VerticalSpacing>
                                        </div>
                                    </VerticalSpacing>
                                )}
                            </VerticalSpacing>
                        )}
                        {showTextEdit && (
                            <>
                                <VerticalSpacing bottom={24}>
                                    <V3TextInput
                                        label="Title"
                                        name="title"
                                        placeholder="Insert title..."
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.title}
                                        characterLimit={60}
                                    />
                                </VerticalSpacing>
                                <VerticalSpacing bottom={24}>
                                    <V3TextArea
                                        name="description"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.description}
                                        label="Insert description (optional)"
                                        characterLimit={200}
                                    />
                                </VerticalSpacing>
                            </>
                        )}
                    </>
                }
            />
        </V3ModalBase>
    );
};

export interface V3MediaEditingModalContainerProps extends MediaEditingFormik {
    modalOpen: boolean;
    onCloseHandler: () => void;
    onMediaEditingSubmit: (data: MediaEditingFormik) => Promise<void>;
    showMediaPreview?: boolean;
    showTextEdit?: boolean;
    modalTitle?: string;
    modalDescription?: string | JSX.Element;
    onDeleteCoverHandler?: () => void;
    onFileUploadHandler: (selectorFiles: FileList) => Promise<string>;
    fallbackImageUrl?: string;
    fallbackTitle?: string;
    fallbackDescription?: string;
    thumbnailImageKey?: string | null;
    showRevert?: boolean;
    removeLabel?: string;
    isCollection?: boolean;
}

export const V3MediaEditingModalContainer = ({ ...rest }: V3MediaEditingModalContainerProps) => {
    const { unsplashImages, unsplashSearchHandler } = unplashHook();

    const unsplashPhotos = unsplashImages.data?.pages || [];
    const unsplashLoading = unsplashImages.isLoading || unsplashImages.isFetching;
    const unsplashLoadMore = unsplashImages.fetchNextPage;
    const unsplashCanFetchmore = unsplashImages.hasNextPage || false;

    return (
        <V3MediaEditingModal
            {...rest}
            unsplashPhotos={unsplashPhotos}
            unsplashLoading={unsplashLoading}
            unsplashLoadMore={unsplashLoadMore}
            unsplashCanFetchmore={unsplashCanFetchmore}
            unsplashSearchHandler={unsplashSearchHandler}
        />
    );
};
